import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, signal } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { CommunitySanction } from '../../models/community.models';

/**
 * Tells a member what is currently in force against them (§18): every active
 * mute/ban/hide with its scope, reason and end, plus the warnings they were
 * given. Fed straight from the overview; renders nothing when both lists are
 * empty, so the shell can place it unconditionally.
 */
@Component({
  selector: 'app-community-sanction-notice',
  standalone: true,
  imports: [CommonModule, TranslocoModule],
  template: `
    <ng-container *transloco="let t">
      @for (s of sanctions ?? []; track s.id) {
        <div class="alert alert-warning py-2 community-sanction">
          <strong>{{ t('community.moderation.notice.' + s.kind) }}</strong>
          <span class="ms-1">
            {{ s.scope_name ? t('community.moderation.notice.in_scope', { scope: s.scope_name }) : t('community.moderation.notice.network_wide') }}
          </span>
          @if (s.until) {
            <span class="ms-1">· {{ t('community.moderation.notice.until', { until: (s.until | date:'medium') }) }}</span>
          }
          <div class="small">{{ t('community.moderation.notice.reason') }}: {{ s.reason }}</div>
        </div>
      }
      @if (warnings?.length) {
        <div class="alert alert-info py-2 community-sanction">
          <button type="button" class="btn btn-link p-0 text-decoration-none fw-semibold" (click)="open.set(!open())">
            ⚠️ {{ t('community.moderation.notice.warnings', { count: warnings!.length }) }}
            <span class="small ms-1">{{ open() ? '▲' : '▼' }}</span>
          </button>
          @if (open()) {
            <ul class="mb-0 mt-2 small">
              @for (w of warnings; track w.id) {
                <li>
                  <span class="text-muted">{{ w.created_at | date:'medium' }}</span>
                  @if (w.scope_name) { <span class="text-muted"> · {{ w.scope_name }}</span> }
                  — {{ w.reason }}
                </li>
              }
            </ul>
          }
        </div>
      }
    </ng-container>
  `,
  styles: [`.community-sanction { margin-bottom: 0.75rem; }`],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunitySanctionNoticeComponent {
  @Input() sanctions: CommunitySanction[] | null | undefined;
  @Input() warnings: CommunitySanction[] | null | undefined;

  open = signal(false);
}
